import {
    AdminPreferences,
    IMetrics,
    InstanceConfigConstants,
    KendraConst,
    SearchInterface,
    Stage
} from "@amzn/ask-legal-domain";
import { Alert, Pagination, Spinner, Tabs, TextContent } from "@amzn/awsui-components-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Builder } from "builder-pattern";
import { AppContext } from "../../setup/context";
import { useLatestRequest } from "../../hooks/use-latest-request";
import { callApi, Loadable } from "../../utils/api-call";
import { useCrossInstanceSearch } from "../../hooks/use-cross-instance-search";
import { useInstanceById } from "../../contexts/InstanceDataContext";
import { PermissionDenied } from "../redirects/PermissionDenied";
import { SearchResultPanelV2 } from "../../components/search/ResultPanelV2";
import { LoadingSpinner } from "../../components/common/loading-spinner";
import { ErrorFlashbar } from "../../components/common/ErrorFlashbar";
import { SourceMatterSmartSearch } from "../../components/mfe/SourceMatterSmartSearch";
import { SmartSearchFilterViewV2 } from "../../components/search/search-components/SmartSearchFilterViewV2";
import { UIModel } from "../../model/ui-model";
import { isStandardResult } from "../../utils/search-utils";
import { describeSearchFilters, getSearchFilterKey } from "../../components/search/search-configs/search-filter-utils-v2";
import { SearchChatContext, useSearchChatContext } from "../../components/quick-agent-chat-panel/search-chat-context-store";
import "../../styles/component/layout/SearchResultPageLayout.scss";
import "../../styles/component/layout/PageBaseLayout.scss";
import "../../styles/component/search/automated-answer.scss";

interface SearchHomeV2Props {
    instanceId: string;
    queryText?: string;
}

const INITIAL_PAGE_SIZE = 10;
const INITIAL_PAGE_NUMBER = 1;

export const SearchHomeV2: React.FC<SearchHomeV2Props> = ({ instanceId, queryText }) => {
    const context = React.useContext(AppContext);
    const { instance, isLoading: isLoadingInstance, hasError: hasInstanceError } = useInstanceById(instanceId);
    const [searchState, setSearchState] = React.useState<Loadable<SearchInterface.SearchQueryResponse>>({ status: "idle" });
    const searchOutput = searchState.status === "loaded" ? searchState.data : undefined;
    const searchError = searchState.status === "error" ? searchState.err : undefined;
    // Only the latest search may update state (filters can change while a search is in flight)
    const searchRequest = useLatestRequest();
    const navigate = useNavigate();
    const isCrossInstanceSearchEnabled = useCrossInstanceSearch();
    const queryInputState = UIModel.State.use<SearchInterface.SearchQueryInput>({});

    const [authors, setAuthors] = React.useState<string[]>([]);
    const [pageLibs, setPageLibs] = React.useState<string[]>([]);
    const [availableInstances, setAvailableInstances] = React.useState<string[]>([]);
    const [instancePreferences, setInstancePreferences] = React.useState<AdminPreferences.InstancePreferences>();
    const [hasLegalOnlyAccess, setHasLegalOnlyAccess] = React.useState(false);

    const initAuthorOptions = React.useCallback(() => {
        const allAuthors: string[] = [];
        searchOutput.result.ResultItems.forEach(r => {
            const author = r.DocumentAttributes.find(a =>
                a.Key === KendraConst.ContainerIngestionKendraAttributes.Default.Authors
            );
            if (author?.Value?.StringListValue) {
                allAuthors.push(...author.Value.StringListValue);
            }
        });
        setAuthors([...new Set(allAuthors)]);
    }, [searchOutput]);

    const initPageLibraries = React.useCallback(() => {
        const pageLibs: string[] = [];
        searchOutput.result.ResultItems.forEach(r => {
            const pageLib = r.DocumentAttributes.find(a =>
                a.Key === KendraConst.ContainerIngestionKendraAttributes.Custom.PageLibraryId
            );
            if (pageLib?.Value?.StringValue) {
                pageLibs.push(pageLib.Value.StringValue);
            }
        });
        setPageLibs([...new Set(pageLibs)]);
    }, [searchOutput]);

    const initAvailableInstances = React.useCallback(() => {
        if (!searchOutput?.result?.FacetResults) return;
        const instanceFacet = searchOutput.result.FacetResults.find(facet =>
            facet.DocumentAttributeKey === KendraConst.ContainerIngestionKendraAttributes.Default.InstanceId
        );
        const instancesFromSearch = instanceFacet?.DocumentAttributeValueCountPairs
            ?.filter(pair => pair.DocumentAttributeValue?.StringValue)
            .map(pair => pair.DocumentAttributeValue.StringValue as string) || [];
        const allInstances = [...instancesFromSearch];
        if (!allInstances.includes(instanceId)) {
            allInstances.push(instanceId);
        }
        setAvailableInstances(allInstances);
    }, [searchOutput, instanceId]);

    const getVisibleResultItems = () => searchOutput.result.ResultItems.filter(isStandardResult);

    const getClientSidePaginatedSearchResults = React.useCallback(() => {
        const { pageNumber, pageSize } = queryInputState.value;
        const startIndex = (pageNumber - 1) * pageSize;
        const endIndex = pageNumber * pageSize;
        const visibleResults = getVisibleResultItems();
        const paginatedResults = visibleResults.slice(startIndex, endIndex);

        return SearchInterface.SearchQueryResponse.create({
            result: {
                ...searchOutput.result,
                ResultItems: paginatedResults,
                // Same source as pagesCount, so the displayed total and the page count always agree
                TotalNumberOfResults: visibleResults.length
            },
            isLegalOnlyAuthorized: searchOutput.isLegalOnlyAuthorized
        });
    }, [queryInputState.value, searchOutput]);

    const constructSearchQueryInput = React.useCallback((
        queryText: string,
        preferences?: AdminPreferences.InstancePreferences
    ): SearchInterface.SearchQueryInput => {
        const instanceConfig = InstanceConfigConstants.getInstanceConfig(
            context.getStage() as Stage,
            instanceId
        );
        const languageCode = instanceConfig
            ? InstanceConfigConstants.getDefaultSearchLanguageCode(context.getStage() as Stage, instanceId)
            : undefined;
        let searchQuery = SearchInterface.SearchQueryInput.create({
            instanceId: instanceId,
            queryText: queryText,
        }, {
            languageCode: languageCode,
            pageSize: INITIAL_PAGE_SIZE,
            pageNumber: INITIAL_PAGE_NUMBER,
            isCrossInstanceSearchLabEnabled: isCrossInstanceSearchEnabled
        });
        if (preferences) {
            if (preferences.search.filterPrefs.showFileType.length > 0) {
                preferences.search.filterPrefs.showFileType.forEach(t => {
                    searchQuery = SearchInterface.SearchQueryInput.applyFilter(searchQuery, {
                        attribute: KendraConst.ContainerIngestionKendraAttributes.Custom.ContentType,
                        operation: "EqualsTo",
                        valueMap: {
                            value: t,
                            valueType: KendraConst.IndexAttributeType.STRING
                        }
                    });
                });
            }
        }
        return searchQuery;
    }, [context, instanceId, isCrossInstanceSearchEnabled]);

    React.useEffect(() => {
        if (!instance) return;
        if (instance.preferences) {
            setInstancePreferences(instance.preferences);
        } else {
            setInstancePreferences(AdminPreferences.DEFAULT_ADMIN_PREFERENCES);
        }
    }, [instance]);

    const [isQueryInitialized, setIsQueryInitialized] = React.useState(false);
    const lastSearchQueryRef = React.useRef<string>("");

    const createQuerySignature = React.useCallback((query: SearchInterface.SearchQueryInput): string => {
        return JSON.stringify({
            text: query.queryText,
            filters: query.filterConfiguration || [],
            page: query.pageNumber,
            sort: query.sortingConfiguration || [],
            legalOnly: query.showStrictlyLegalOnly
        });
    }, []);

    const executeSearchImmediate = React.useCallback((query: SearchInterface.SearchQueryInput) => {
        const querySignature = createQuerySignature(query);
        if (querySignature === lastSearchQueryRef.current) {
            return;
        }
        lastSearchQueryRef.current = querySignature;
        const isCurrent = searchRequest.begin();
        setSearchState({ status: "loading" });
        callApi(context.getSearchAPI().search, query).then(result => {
            if (!isCurrent()) return;
            setSearchState(result.ok
                ? { status: "loaded", data: result.data }
                : { status: "error", err: result.err });
        });
        // Fire-and-forget, like the live page
        callApi(
            context.getCommonAPI().recordViewHistory,
            IMetrics.RecordViewHistoryInput.create({
                url: `/search/${instanceId}?queryText=${queryText}`,
                entityId: instanceId,
                type: "Search"
            })
        );
    }, [createQuerySignature, context, searchRequest, instanceId, queryText]);

    React.useEffect(() => {
        if (!instancePreferences) return;

        queryInputState.setValue(constructSearchQueryInput(queryText, instancePreferences));
        setAuthors([]);
        setPageLibs([]);
        setIsQueryInitialized(true);
    }, [queryText, instancePreferences, constructSearchQueryInput]);

    React.useEffect(() => {
        if (!isQueryInitialized || !queryInputState.value?.queryText?.length || !instancePreferences) {
            return;
        }

        executeSearchImmediate(queryInputState.value);
    }, [queryInputState.value, instancePreferences, isQueryInitialized, executeSearchImmediate]);

    React.useEffect(() => {
        if (searchState.status !== "loaded" || !searchOutput) return;

        if (authors.length === 0) {
            initAuthorOptions();
        }
        if (pageLibs.length === 0) {
            initPageLibraries();
        }
        if (availableInstances.length === 0 && isCrossInstanceSearchEnabled) {
            initAvailableInstances();
        }
        setHasLegalOnlyAccess(searchOutput.isLegalOnlyAuthorized);
    }, [
        searchState.status,
        searchOutput,
        authors.length,
        pageLibs.length,
        availableInstances.length,
        isCrossInstanceSearchEnabled,
        initAuthorOptions,
        initPageLibraries,
        initAvailableInstances
    ]);

    // Publish the current search (term, instance, filters; not sort or page) for the Quick chat panel.
    // Filter lines carry raw ids on purpose: the chat harness uses them as MCP tool arguments.
    const { setContext: setSearchChatContext, clearContext: clearSearchChatContext } = useSearchChatContext();
    const activeQuery = queryInputState.value;
    const filterKey = activeQuery ? getSearchFilterKey(activeQuery) : "";
    const publishedSearchChatContext = React.useRef<SearchChatContext | undefined>(undefined);
    React.useEffect(() => {
        if (!activeQuery?.queryText) return;
        publishedSearchChatContext.current = {
            queryText: activeQuery.queryText,
            instanceId,
            filters: describeSearchFilters(activeQuery),
            filterKey
        };
        setSearchChatContext(publishedSearchChatContext.current);
    }, [activeQuery?.queryText, instanceId, filterKey]);
    React.useEffect(() => () => clearSearchChatContext(publishedSearchChatContext.current), []);

    // Validation check after all hooks have been called (same as GlobalSearchHomeV2)
    if (!queryText?.trim()) {
        return (
            <div className="top-margin-5p">
                <Alert
                    type="error"
                    header="Cannot search for an empty query"
                >
                    <TextContent>
                        Please provide a valid search query and try again.
                    </TextContent>
                </Alert>
            </div>
        );
    }

    // Back to the page's starting filters; the chosen sort and page size are kept
    const clearFilters = () => {
        const current = queryInputState.value;
        queryInputState.setValue(
            Builder<SearchInterface.SearchQueryInput>(constructSearchQueryInput(current.queryText, instancePreferences))
                .sortingConfiguration(current.sortingConfiguration)
                .pageSize(current.pageSize)
                .build()
        );
    };

    // The Pathfinder results (filters bar + results), rendered once below: either on its own or inside the
    // "Pathfinder Results" tab when the instance also has source instances.
    const renderPathfinderResults = () => (
        <>
            <SmartSearchFilterViewV2
                preferences={instancePreferences.search}
                queryInputState={queryInputState}
                authors={authors}
                pageLibraries={pageLibs}
                availableInstances={availableInstances}
                hasLegalOnlyAccess={hasLegalOnlyAccess}
                isSearchRunning={searchState.status === "loading"}
                defaultFileTypes={instancePreferences.search.filterPrefs.showFileType}
                onClearFilters={clearFilters}
            />
            <div className="pf-search-results">
                {searchState.status === "loading" &&
                    <div className="vertical-center top-margin-5p">
                        <LoadingSpinner msg={`Searching for ${queryInputState.value.queryText} ...`}/>
                    </div>
                }
                {searchState.status === "error" && (
                    searchError.code === 403 ?
                        <PermissionDenied entityId={instanceId} accessType="HomePage"/> :
                        <ErrorFlashbar error={searchError}/>
                )}
                {searchState.status === "loaded" && !!searchOutput && (
                    <React.Fragment>
                        <h4>
                            Results for <em>"{queryInputState.value.queryText}"</em>
                        </h4>
                        {!!searchOutput.result.SpellCorrectedQueries?.length &&
                            <div className="search-result-spell-correction">
                                {searchOutput.result.SpellCorrectedQueries.map(spellCorrectionQuery => {
                                    const suggested = spellCorrectionQuery.SuggestedQueryText ?? "";
                                    const suggestedHref = `#/search/${instanceId}?queryText=${encodeURIComponent(suggested)}`;
                                    return (
                                        <div key={suggested}>
                                            Do you mean "<a href={suggestedHref} onClick={event => {
                                                event.preventDefault();
                                                queryInputState.setValue(
                                                    constructSearchQueryInput(suggested, instancePreferences)
                                                );
                                                // Update URL without triggering page reload
                                                navigate(`/search/${instanceId}?queryText=${encodeURIComponent(suggested)}`, { replace: true });
                                            }}>{suggested}</a>"?
                                        </div>
                                    );
                                })}
                                <br />
                            </div>
                        }
                        <SearchResultPanelV2
                            results={getClientSidePaginatedSearchResults()}
                            instanceId={instanceId}
                            queryInputState={queryInputState}
                        />
                        <nav className="centered-pagination" aria-label="pagination">
                            <Pagination
                                ariaLabels={{
                                    nextPageLabel: "Next page",
                                    previousPageLabel: "Previous page",
                                    pageLabel: pageNumber =>
                                        `Page ${pageNumber} of all pages`
                                }}
                                currentPageIndex={queryInputState.value.pageNumber}
                                onChange={({ detail }) =>
                                    queryInputState.setValue(
                                        Builder<SearchInterface.SearchQueryInput>(queryInputState.value)
                                            .pageNumber(detail.currentPageIndex)
                                            .build()
                                    )
                                }
                                pagesCount={Math.ceil(
                                    getVisibleResultItems().length / queryInputState.value.pageSize
                                )}
                            />
                        </nav>
                    </React.Fragment>
                )}
            </div>
        </>
    );

    return (
        <React.Fragment>
            {hasInstanceError &&
                <div className="top-margin-5p">
                    <PermissionDenied
                        entityId={instanceId}
                        accessType="HomePage"
                    />
                </div>
            }
            {isLoadingInstance &&
                <div className="vertical-center top-margin-5p">
                    <Spinner />&nbsp;Loading search module...
                </div>
            }
            {instance && queryInputState.value &&
                <div className="pf-search-page">
                    {!instancePreferences?.search?.sourceInstances?.length && renderPathfinderResults()}
                    {!!instancePreferences.search.sourceInstances?.length && (
                        <Tabs tabs={[
                            { label: "Pathfinder Results", id: "Pathfinder", content:
                                <div className="pf-search-tab-panel">
                                    {renderPathfinderResults()}
                                </div>
                            },
                            instancePreferences.search.sourceInstances?.length && {
                                label: "Source Results",
                                id: "Source",
                                content: <>
                                    <SourceMatterSmartSearch
                                        instanceId={instancePreferences?.search?.sourceInstances?.[0]?.id}
                                        filterText={queryInputState?.value?.queryText}
                                    />
                                </>
                            }]}
                        />
                    )}
                </div>
            }
        </React.Fragment>
    );
};
